import {
  EFollowType,
  FollowItemProps,
  FollowType,
} from '@bonhams1793/bonhams-typescript'

import { useGetUser } from '@nx/api'

import { useFollowItem } from '../useFollowItem'
import {
  UseGetAllFollowedResponse,
  useGetAllFollowed,
} from '../useGetAllFollowed'
import { useUnfollowItem } from '../useUnfollowItem'

interface FollowHookProps {
  type: EFollowType
  itemId: number | string
  metadata?: FollowItemProps['metadata']
  baseURL: string
  openLogin?: () => void
}

function getResponseForType(
  followedItems: UseGetAllFollowedResponse,
  type: EFollowType
) {
  switch (type) {
    case FollowType.artist:
      return followedItems.artists
    case FollowType.auction:
      return followedItems.auctions
    case FollowType.department:
      return followedItems.departments
    default:
      return {
        ...followedItems.lots,
        data: followedItems.lots.data
          ? (Object.values(followedItems.lots.data).flat() as number[])
          : [],
      }
  }
}

export const useFollow = ({
  type,
  itemId,
  metadata,
  baseURL,
  openLogin,
}: FollowHookProps) => {
  const { data: user } = useGetUser(baseURL)

  const followedItems = useGetAllFollowed({
    userId: user?.customerId,
    baseUrl: baseURL,
  })

  const followItem = useFollowItem(baseURL)
  const unfollowItem = useUnfollowItem(baseURL)

  const followedItemsByType = getResponseForType(followedItems, type)

  const isFollowing = Array.isArray(followedItemsByType?.data)
    ? followedItemsByType.data.some((item) => String(item) === String(itemId))
    : false

  function toggleFollow(e: React.SyntheticEvent) {
    if (!user) {
      e.preventDefault()
      openLogin?.()
    } else {
      if (isFollowing) {
        unfollowItem.mutate({ userId: user.customerId, type, itemId, metadata })
      } else {
        followItem.mutate({ userId: user.customerId, type, itemId, metadata })
      }
    }
  }

  return {
    toggleFollow,
    isFollowing,
    isLoading: followItem.isPending || unfollowItem.isPending,
  }
}
